import {
	Button,
	Content,
	ContentVariants,
	Divider,
	Flex,
	FlexItem,
	Icon,
	Progress,
	ProgressMeasureLocation,
	ProgressStep,
	ProgressStepper,
	Split,
	SplitItem,
	Stack,
	StackItem,
} from "@patternfly/react-core";
import {
	CheckCircleIcon,
	ExclamationTriangleIcon,
	InProgressIcon,
	OutlinedQuestionCircleIcon,
	ResourcesAlmostEmptyIcon,
} from "@patternfly/react-icons";
import type { CSSProperties, FC } from "react";
import { EPartnerValidationProgress } from "../../constants/product";
import { useProductMetadata } from "../../hooks/useProductMetadata";
import { validateAttachedProjects } from "../../utils/attachedProjectValidator";
import "./ProductValidationsAndCertifications.scss";
import { ToolTip } from "@rhc-shared-components/tool-tip";
import { Redirect, Route, Switch, useHistory, useRouteMatch } from "react-router-dom";
import type { ProductRouteParams } from "../../constants/routerconstants";
import Env from "../../utils/env";
import AddComponents from "./AddComponents";
import CompleteCertificationTesting from "./CompleteCertificationTesting";
import SupportStatement from "./SupportStatement";

type ProductCertificationsAndValidationsProps = {};

const ProductValidationsAndCertifications: FC<ProductCertificationsAndValidationsProps> = () => {
	const {
		productDetail,
		activeCertificationListState,
		partnerValidationProgress,
		componentMetadata: { hasValidated, isCertifiedProduct },
	} = useProductMetadata();
	const match = useRouteMatch<ProductRouteParams>();
	const history = useHistory();
	const isValidated = hasValidated || partnerValidationProgress === EPartnerValidationProgress.Validated;
	const isProductCertified =
		validateAttachedProjects(productDetail, activeCertificationListState, false) && isCertifiedProduct;
	const hasActiveUnvalidatedProjects = (activeCertificationListState?.length ?? 0) > 0 && !hasValidated;
	enum TabIds {
		supportStatement = "support-statement",
		addComponents = "add-components",
		completeCertificationTesting = "complete-certification-testing",
	}

	const MatchedRoutes = {
		[TabIds.supportStatement]: `${match.path}/${TabIds.supportStatement}`,
		[TabIds.addComponents]: `${match.path}/${TabIds.addComponents}`,
		[TabIds.completeCertificationTesting]: `${match.path}/${TabIds.completeCertificationTesting}`,
	};

	const setCurrentTab = (tabid: TabIds) => {
		history.push(`${Env.routePrefix}/products/${match?.params?.productId}/validations-and-certifications/${tabid}`);
	};
	let statusText = "Not Validated or Certified";
	if (isValidated && !isProductCertified) {
		statusText = "Validated";
	} else if (isProductCertified) {
		statusText = "Certified";
	}
	const ColorConst = {
		success: "#3E8635",
		started: "#D1F1BB",
		notStarted: "#D2D2D2",
	};
	return (
		<Flex
			grow={{ default: "grow" }}
			flexWrap={{
				default: "wrap",
				md: "nowrap",
				lg: "nowrap",
				xl: "nowrap",
				"2xl": "nowrap",
			}}
			data-analytics-category="Product Info|Tabs|Validations & certifications"
			className="product-certifications-and-validations"
		>
			<FlexItem span={3} className="product-certifications-and-validations__left-side">
				<Stack hasGutter={true}>
					<StackItem>
						<Stack hasGutter className="product-certifications-and-validations__left-side__tile">
							<StackItem>
								<Content>
									<Content
										component={ContentVariants.h4}
										className={"product-certifications-and-validations__left-side__tooltip-icon-text-wrapper"}
									>
										{statusText}
										<ToolTip buttonAriaLabel={"Show more information "} buttonContent={<OutlinedQuestionCircleIcon />}>
											Validated products are tested and supported by Partners while Red Hat certified products are
											tested and supported collaboratively. Validating or certifying your product is necessary to
											publish it to the Red Hat Ecosystem Catalog.{" "}
											<Button
												isInline={true}
												variant="link"
												component={"a"}
												href="https://docs.redhat.com/en/documentation/red_hat_partner_certification/2025/html/red_hat_partner_validation_guide/index"
												target="_blank"
											>
												Learn more
											</Button>
										</ToolTip>
									</Content>
								</Content>
							</StackItem>
							<StackItem>
								<Split hasGutter className="product-certifications-and-validations__left-side__progress">
									<SplitItem className="product-certifications-and-validations__left-side__progress__first">
										<Progress
											aria-label={"validation status"}
											value={
												partnerValidationProgress === EPartnerValidationProgress.Validated
													? 100
													: partnerValidationProgress === EPartnerValidationProgress.Started
														? 50
														: 0
											}
											className="product-certifications-and-validations__left-side__progress__graph"
											size="sm"
											measureLocation={ProgressMeasureLocation.none}
											style={
												{
													"--pf-v6-c-progress__indicator--BackgroundColor": ColorConst.success,
													"--pf-v6-c-progress__bar--BackgroundColor":
														partnerValidationProgress === EPartnerValidationProgress.Started
															? ColorConst.started
															: ColorConst.notStarted,
												} as CSSProperties
											}
										/>
									</SplitItem>
									<SplitItem isFilled={true}>
										<Progress
											aria-label={"certification status"}
											value={isProductCertified ? 100 : hasActiveUnvalidatedProjects ? 50 : 0}
											className="product-certifications-and-validations__left-side__progress__graph"
											size="sm"
											measureLocation={ProgressMeasureLocation.none}
											style={
												{
													"--pf-v6-c-progress__indicator--BackgroundColor": ColorConst.success,
													"--pf-v6-c-progress__bar--BackgroundColor": `${hasActiveUnvalidatedProjects ? ColorConst.started : ColorConst.notStarted}`,
												} as CSSProperties
											}
										/>
									</SplitItem>
								</Split>
							</StackItem>
						</Stack>
					</StackItem>
					<StackItem>
						<ProgressStepper
							aria-label="Certification  progress"
							isVertical={true}
							className="product-certifications-and-validations__left-side__progress-stepper"
						>
							<ProgressStep
								variant="default"
								id="validation"
								titleId="validation"
								aria-label="Validation"
								className="product-certifications-and-validations__left-side__progress-stepper__hidden_step"
							>
								Validation&nbsp;&nbsp;
								{partnerValidationProgress === EPartnerValidationProgress.Validated ? (
									<Icon isInline status="success">
										<CheckCircleIcon />
									</Icon>
								) : (
									<Icon isInline status="warning">
										<ExclamationTriangleIcon />
									</Icon>
								)}
							</ProgressStep>
							<ProgressStep
								variant={
									partnerValidationProgress === EPartnerValidationProgress.Validated
										? "success"
										: partnerValidationProgress === EPartnerValidationProgress.Started
											? "default"
											: match?.params?.tabid === TabIds.supportStatement
												? "info"
												: "default"
								}
								{...(partnerValidationProgress === EPartnerValidationProgress.Started && {
									icon: <InProgressIcon />,
								})}
								className="product-certifications-and-validations__left-side__progress-stepper__clickable_step"
								id={TabIds.supportStatement}
								titleId={TabIds.supportStatement}
								isCurrent={match?.params?.tabid === TabIds.supportStatement}
								onClick={() => setCurrentTab(TabIds.supportStatement)}
								aria-label="Support Statement"
							>
								Support statement
							</ProgressStep>
							<ProgressStep
								variant="default"
								id="certification"
								titleId="certification"
								aria-label="certification"
								className="product-certifications-and-validations__left-side__progress-stepper__hidden_step"
							>
								Certification&nbsp;&nbsp;
								{isProductCertified ? (
									<Icon isInline status="success">
										<CheckCircleIcon />
									</Icon>
								) : (
									<Icon isInline>
										<ResourcesAlmostEmptyIcon />
									</Icon>
								)}
							</ProgressStep>
							<ProgressStep
								variant={
									isProductCertified
										? "success"
										: hasActiveUnvalidatedProjects
											? "success"
											: match?.params?.tabid === TabIds.addComponents
												? "info"
												: "default"
								}
								{...(hasValidated && !isProductCertified && { icon: <InProgressIcon /> })}
								className="product-certifications-and-validations__left-side__progress-stepper__clickable_step"
								id={TabIds.addComponents}
								titleId={TabIds.addComponents}
								isCurrent={match?.params?.tabid === TabIds.addComponents}
								onClick={() => setCurrentTab(TabIds.addComponents)}
								aria-label="add components"
							>
								Add components
							</ProgressStep>
							<ProgressStep
								variant={
									isProductCertified
										? "success"
										: match?.params?.tabid === TabIds.completeCertificationTesting
											? "info"
											: "default"
								}
								className="product-certifications-and-validations__left-side__progress-stepper__clickable_step"
								id={TabIds.completeCertificationTesting}
								titleId={TabIds.completeCertificationTesting}
								isCurrent={match?.params?.tabid === TabIds.completeCertificationTesting}
								onClick={() => {
									if (activeCertificationListState?.length > 0) {
										setCurrentTab(TabIds.completeCertificationTesting);
									} else {
										setCurrentTab(TabIds.addComponents);
									}
								}}
								aria-label="complete certification testing"
							>
								Complete certification testing
							</ProgressStep>
						</ProgressStepper>
					</StackItem>
				</Stack>
			</FlexItem>
			<Divider
				className="product-certifications-and-validations__divider"
				component="div"
				orientation={{
					default: "horizontal",
					sm: "horizontal",
					md: "horizontal",
					lg: "vertical",
					xl: "vertical",
					"2xl": "vertical",
				}}
			/>
			<FlexItem span={8} className="product-certifications-and-validations__right-side" grow={{ default: "grow" }}>
				<Switch>
					<Redirect
						exact
						from={`${Env.routePrefix}/products/${match?.params?.productId}/validations-and-certifications`}
						to={`${Env.routePrefix}/products/${match?.params?.productId}/validations-and-certifications/${TabIds.supportStatement}`}
					/>
					{activeCertificationListState?.length === 0 && (
						<Redirect
							exact
							from={`${Env.routePrefix}/products/${match?.params?.productId}/validations-and-certifications/${TabIds.completeCertificationTesting}`}
							to={`${Env.routePrefix}/products/${match?.params?.productId}/validations-and-certifications/${TabIds.addComponents}`}
						/>
					)}
					<Route path={MatchedRoutes[TabIds.supportStatement]}>
						<SupportStatement
							onNext={() => setCurrentTab(TabIds.addComponents)}
							navigateToComponentTesting={() => setCurrentTab(TabIds.completeCertificationTesting)}
						/>
					</Route>
					<Route path={MatchedRoutes[TabIds.addComponents]}>
						<AddComponents
							onNext={() => setCurrentTab(TabIds.completeCertificationTesting)}
							goToSupportStatements={() => setCurrentTab(TabIds.supportStatement)}
						/>
					</Route>
					<Route path={MatchedRoutes[TabIds.completeCertificationTesting]}>
						<CompleteCertificationTesting goToAddComponents={() => setCurrentTab(TabIds.addComponents)} />
					</Route>
				</Switch>
			</FlexItem>
		</Flex>
	);
};

export default ProductValidationsAndCertifications;
